import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { contract } from '@aviv_price-pages/brand';
import { SidePanelLayoutBlockEnum } from '@aviv_price-pages/brand/src/common';
import { useThorView } from '@aviv-dxp/react-thor';
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { Box } from '@gemini/core';
import { MapIcon } from '@gemini/icons';
import { Button } from '@gemini/ui';
import EstimationModal from '@/components/EstimationModal';
import PriceCardA from '@/components/PriceCard/PriceCardA';
import PriceCardB from '@/components/PriceCard/PriceCardB';
import { MediumCta } from '@/entities/enums/MediumCta';
import { getAssetsPath } from '@/helpers/getAssetsPath';
import { pricesToDisplay } from '@/helpers/pricesToDisplay/pricesToDisplay';
import { getMediumAttribute } from '@/helpers/tracking/tracking';
import { useMobileViewDetect } from '@/hooks/useMobileViewDetect/useMobileViewDetect';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { usePageTitle } from '@/hooks/usePageTitle/usePageTitle';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
import { getPageTitle, useTrackingProps } from '@/hooks/useTrackingProps/useTrackingProps';
import { isServer } from '@/utils/isServer';
import { smoothScrollToTop } from '@/utils/smoothScrollToTop';
import { UnavailablePrice } from './UnavailablePrice';
export const PriceBlock = ({ pricePageContext }) => {
    const { sidebarOpen, setSidebarOpen } = usePageLayoutContext();
    const { t } = useShellTranslation();
    const { language } = useLocale();
    const { sidePanelConfig } = contract;
    const updateDateRef = useRef(null);
    const { isMobileView } = useMobileViewDetect();
    const viewProps = useTrackingProps();
    const view = useThorView();
    const { place, prices, isDuplicatePlaceName } = pricePageContext;
    const title = usePageTitle(place, isDuplicatePlaceName);
    const firstDayOfCurrentMonthDate = new Date();
    firstDayOfCurrentMonthDate.setDate(1);
    const updateDate = t('price_block.info_view.update_date', {
        replace: {
            date: new Intl.DateTimeFormat(language, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            }).format(new Date(firstDayOfCurrentMonthDate.toString())),
        },
    }).replace(/&#x2F;/g, '/');
    useEffect(() => {
        const date = new Intl.DateTimeFormat(language, {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        }).format(new Date(firstDayOfCurrentMonthDate.toString()));
        const updateDateForClient = t('price_block.info_view.update_date', {
            replace: { date },
        }).replace(/&#x2F;/g, '/');
        if (updateDateRef.current) {
            updateDateRef.current.textContent = updateDateForClient;
        }
    });
    const handleButtonSwitch = () => {
        smoothScrollToTop();
        view({
            viewProps: {
                ...viewProps,
                av_medium: getMediumAttribute(sidebarOpen ? MediumCta.SWITCH_MAP_VIEW : MediumCta.SWITCH_INFO_VIEW, pricePageContext.place.type, pricePageContext.place.type),
                // av_pagetitle is calculated with the future value of sidebarOpen
                av_pagetitle: getPageTitle(pricePageContext.place, isMobileView, !sidebarOpen),
            },
        });
        setSidebarOpen((sideBarWasOpen) => !sideBarWasOpen);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, { as: "h1", textAlign: "center", typography: "typography.headline.24.bold", children: title }), prices?.sell ? (_jsxs(Box, { children: [_jsx(Box, { display: "flex", flexDirection: {
                            'breakpoint.base': 'column',
                            'breakpoint.md': 'row',
                            'breakpoint.lg': 'column',
                            'breakpoint.xxl': 'row',
                        }, gap: "spacing.16", children: pricesToDisplay(prices?.sell).map((item, index) => sidePanelConfig.blockConfig[SidePanelLayoutBlockEnum.PRICE].variant ===
                            'A' ? (_jsx(PriceCardA, { prices: item }, index)) : (_jsx(PriceCardB, { prices: item }, index))) }), _jsx(Box, { ref: updateDateRef, as: "p", marginVertical: "spacing.8", typography: "typography.body.16.regular", children: updateDate })] })) : (_jsx(Box, { display: "flex", flexDirection: "column", alignItems: "center", children: _jsx(UnavailablePrice, {}) })), _jsx(EstimationModal, {}), isMobileView && !isServer() && (_jsxs(Box, { position: "relative", marginTop: "spacing.32", children: [_jsx(Box, { as: "img", objectFit: "cover", width: "100%", height: "92px", src: getAssetsPath('map.png'), borderRadius: "radius.16", loading: "eager" }), _jsx(Box, { position: "absolute", top: "50%", left: "50%", unsafe_style: { transform: 'translate(-50%, -50%)' }, children: _jsx(Button, { icon: MapIcon, iconPosition: "start", variant: "secondary-floating", size: "40", onPress: () => handleButtonSwitch(), children: t('button_switch.map_view') }) })] }))] }));
};
